const transliterationMap: Record<string, string> = {
    ß: "ss",
    Æ: "ae",
    æ: "ae",
    Ø: "o",
    ø: "o",
    Œ: "oe",
    œ: "oe",
    Ł: "l",
    ł: "l",
    Đ: "d",
    đ: "d",
    Þ: "th",
    þ: "th",
    İ: "i",
    ı: "i",
    Ș: "s",
    ș: "s",
    Ț: "t",
    ț: "t",
};

const transliterationPattern = /[ßÆæØøŒœŁłĐđÞþİıȘșȚț]/g;

export const normalizeForSearch = (value: string): string => {
    const transliterated = value.replace(
        transliterationPattern,
        (character) => transliterationMap[character] || character
    );

    return transliterated
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
};

export const tokenizeForSearch = (value: string): string[] => {
    return normalizeForSearch(value)
        .replace(/[^a-z0-9\s]/g, " ")
        .split(/\s+/)
        .filter(Boolean);
};

const getMaxDistance = (length: number): number => {
    if (length <= 4) return 0;
    if (length <= 7) return 1;
    return 2;
};

const levenshteinDistance = (left: string, right: string): number => {
    const leftLength = left.length;
    const rightLength = right.length;

    if (leftLength === 0) return rightLength;
    if (rightLength === 0) return leftLength;

    const matrix: number[][] = Array.from({ length: leftLength + 1 }, () =>
        Array(rightLength + 1).fill(0)
    );

    for (let row = 0; row <= leftLength; row += 1) {
        matrix[row][0] = row;
    }

    for (let column = 0; column <= rightLength; column += 1) {
        matrix[0][column] = column;
    }

    for (let row = 1; row <= leftLength; row += 1) {
        for (let column = 1; column <= rightLength; column += 1) {
            const substitutionCost =
                left[row - 1] === right[column - 1] ? 0 : 1;

            matrix[row][column] = Math.min(
                matrix[row - 1][column] + 1,
                matrix[row][column - 1] + 1,
                matrix[row - 1][column - 1] + substitutionCost
            );
        }
    }

    return matrix[leftLength][rightLength];
};

const isTokenMatch = (queryToken: string, sourceToken: string): boolean => {
    if (sourceToken.includes(queryToken)) {
        return true;
    }

    const maxDistance = getMaxDistance(queryToken.length);
    if (maxDistance === 0) {
        return false;
    }

    if (Math.abs(queryToken.length - sourceToken.length) > maxDistance) {
        return false;
    }

    if (queryToken[0] !== sourceToken[0]) {
        return false;
    }

    return levenshteinDistance(queryToken, sourceToken) <= maxDistance;
};

export const isFuzzyTextMatch = (
    sourceText: string,
    queryText: string
): boolean => {
    const normalizedQuery = normalizeForSearch(queryText);
    if (!normalizedQuery) {
        return true;
    }

    const normalizedSource = normalizeForSearch(sourceText);
    if (normalizedSource.includes(normalizedQuery)) {
        return true;
    }

    const queryTokens = tokenizeForSearch(normalizedQuery);
    const sourceTokens = tokenizeForSearch(sourceText);

    return queryTokens.every((queryToken) =>
        sourceTokens.some((sourceToken) =>
            isTokenMatch(queryToken, sourceToken)
        )
    );
};
